import { useState, useRef, useEffect } from 'react';
import { Check, LogOut, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { APP_VERSION } from '../config/appVersion';

interface HeaderProps {
  title: string;
  subtitle?: string;
}

// Mapping des avatars par email ou nom
const avatarMap: Record<string, string> = {
  // Dany Bourassa
  'dbourassa@bzinc.ca': '/DB.png',
  'dany bourassa': '/DB.png',
  'Dany Bourassa': '/DB.png',
  // Eric Hains
  'ehains@bzinc.ca': '/EH.png',
  'eric hains': '/EH.png',
  'Eric Hains': '/EH.png',
  // Jean-Pierre Leclerc
  'jpleclerc@bzinc.ca': '/JP.png',
  'jleclerc@bzinc.ca': '/JP.png',
  'jean-pierre leclerc': '/JP.png',
  'Jean-Pierre Leclerc': '/JP.png',
  // Antoine Giroux
  'agiroux@bzinc.ca': '/AG-transparent.png',
  'antoine giroux': '/AG-transparent.png',
  'Antoine Giroux': '/AG-transparent.png',
  // Eric Durand
  'edurand@bzinc.ca': '/ED.png',
  'eric durand': '/ED.png',
  'Eric Durand': '/ED.png',
  // Emilie Quintal
  'equal@bzinc.ca': '/EQ.png',
  'emilie quintal': '/EQ.png',
  'Emilie Quintal': '/EQ.png',
  // Francis Marquis
  'fmarquis@bzinc.ca': '/FM.png',
  'francis marquis': '/FM.png',
  'Francis Marquis': '/FM.png',
  // Francois Delisle
  'fdelisle@bzinc.ca': '/FP.png',
  'francois delisle': '/FP.png',
  'Francois Delisle': '/FP.png',
  // Jerome Martel
  'jmartel@bzinc.ca': '/JM.png',
  'jerome martel': '/JM.png',
  'Jerome Martel': '/JM.png',
  // Rosalie Emond
  'rosalie emond': '/RE.png',
  'Rosalie Emond': '/RE.png',
  // Jean-Michel Caouette
  'jmcaouette@bzinc.ca': '/JMC.png',
  'jcaouette@bzinc.ca': '/JMC.png',
  'jean-michel caouette': '/JMC.png',
  'Jean-Michel Caouette': '/JMC.png',
  // Melissa Pelletier
  'mpelletier@bzinc.ca': '/MP.png',
  'melissa pelletier': '/MP.png',
  'Melissa Pelletier': '/MP.png',
  // Richard Delisle
  'rdelisle@bzinc.ca': '/RD.png',
  'richard delisle': '/RD.png',
  'Richard Delisle': '/RD.png',
  // Vincent Bourassa
  'vbourassa@bzinc.ca': '/VB.png',
  'vincent bourassa': '/VB.png',
  'Vincent Bourassa': '/VB.png',
  // Stéphane Paradis
  'stephane.paradis@bzinc.ca': '/SP.png',
  'stephane paradis': '/SP.png',
  'Stéphane Paradis': '/SP.png',
};

// Fonction pour obtenir l'avatar d'un utilisateur
function getUserAvatar(user: { email?: string; name?: string }): string | null {
  if (!user) return null;
  
  // Vérifier par email (insensible à la casse)
  if (user.email) {
    const emailLower = user.email.toLowerCase();
    if (avatarMap[emailLower]) {
      return avatarMap[emailLower];
    }
  }
  
  // Vérifier par nom (exact, minuscule et avec première lettre majuscule)
  if (user.name) {
    // Essayer le nom tel quel
    if (avatarMap[user.name]) {
      return avatarMap[user.name];
    }
    // Essayer en minuscules
    if (avatarMap[user.name.toLowerCase()]) {
      return avatarMap[user.name.toLowerCase()];
    }
    // Essayer avec première lettre majuscule
    const nameFormatted = user.name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    if (avatarMap[nameFormatted]) {
      return avatarMap[nameFormatted];
    }
  }
  
  return null;
}

export default function Header({ title, subtitle }: HeaderProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [microsoftLinked, setMicrosoftLinked] = useState<boolean | null>(null);
  const { logout, user } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Lien et statut Microsoft (PRIsME BZ — indépendant du module Projets)
  const apiBase = (import.meta.env.VITE_API_ORIGIN as string) || '';
  const microsoftLinkHref = `${apiBase}/api/auth/microsoft/link`;

  // Vérifier si le compte Microsoft est lié quand le menu s'ouvre
  useEffect(() => {
    if (!isUserMenuOpen || !user) return;
    const statusUrl = `${apiBase || ''}/api/auth/microsoft/status`;
    fetch(statusUrl, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setMicrosoftLinked(!!data?.linked))
      .catch(() => setMicrosoftLinked(false));
  }, [isUserMenuOpen, user, apiBase]);

  // Fermer le menu user quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header className="bg-white shadow-sm mb-2">
        <div className="px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-bz-dark">{title}</h1>
            {subtitle && (
              <p className="text-gray-500 text-sm mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-500 shadow-sm"
              title={`Version ${APP_VERSION}`}
            >
              v{APP_VERSION}
            </div>
            {/* Avatar User avec Dropdown */}
            {user && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200"
                  title={user.name}
                >
                  {(() => {
                    const avatarPath = getUserAvatar(user);
                    if (avatarPath) {
                      return (
                        <div className="w-10 h-10 rounded-full ring-2 ring-purple-200 hover:ring-purple-300 transition-all overflow-hidden bg-gray-100">
                          <img 
                            src={avatarPath} 
                            alt={user.name || 'Avatar'} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback si l'image ne charge pas
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center"><span class="text-white font-bold text-sm">${user.name?.split(' ').map((n: string) => n[0]).join('') || 'U'}</span></div>`;
                              }
                            }}
                          />
                        </div>
                      );
                    }
                    // Fallback avec initiales
                    return (
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center ring-2 ring-purple-200 hover:ring-purple-300 transition-all">
                        <span className="text-white font-bold text-sm">{user.name?.split(' ').map((n: string) => n[0]).join('') || 'U'}</span>
                      </div>
                    );
                  })()}
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{user.role}</p>
                      <p className="text-xs text-gray-400 mt-1">{user.email}</p>
                    </div>
                    <a
                      href={microsoftLinkHref}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100 no-underline"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="font-medium flex-1">Lier mon compte Microsoft pour l'envoi de courriels (PRIsME BZ)</span>
                      {microsoftLinked === true && (
                        <Check className="w-4 h-4 shrink-0 text-green-600" aria-hidden />
                      )}
                    </a>
                    <button
                      onClick={() => {
                        logout();
                        setIsUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="font-medium">Se déconnecter</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}



